/**
 * Documents search page — global search across all job documents.
 * Phase 25: Search by serial number, job number, part number, customer, document type.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { searchDocuments, downloadFile } from '../api/files';
import { Button, LoadingSpinner, EmptyState } from '../components/common';

const DOCUMENT_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'calibration_cert', label: 'Calibration Certificate' },
  { value: 'pull_test_cert', label: 'Pull Test Certificate' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'material_cert', label: 'Material Cert / Mill Cert' },
  { value: 'drawing', label: 'Drawing' },
  { value: 'spec_sheet', label: 'Spec Sheet' },
  { value: 'other', label: 'Other' },
];

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [docType, setDocType] = useState(searchParams.get('type') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const doSearch = useCallback(async (q, type) => {
    setLoading(true);
    setSearched(true);
    try {
      const params = {};
      if (q.trim()) params.search = q.trim();
      if (type) params.document_type = type;
      // Only search job-linked documents
      params.linked_entity_type = 'job';
      const data = await searchDocuments(params);
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search on mount if URL has params
  useEffect(() => {
    const q = searchParams.get('q') || '';
    const type = searchParams.get('type') || '';
    if (q || type) {
      setQuery(q);
      setDocType(type);
      doSearch(q, type);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearch(e) {
    e?.preventDefault();
    const params = {};
    if (query.trim()) params.q = query.trim();
    if (docType) params.type = docType;
    setSearchParams(params);
    doSearch(query, docType);
  }

  async function handleDownload(file) {
    try {
      const result = await downloadFile(file.id);
      if (result?.download_url) {
        window.open(result.download_url, '_blank');
      }
    } catch {
      alert('Download failed');
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Documents</h1>
        <p className="text-sm text-gray-500 mt-1">
          Search across all job documents by serial number, job number, part number, customer, or filename.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <label className="text-xs font-medium text-gray-600 mb-1 block">Search</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Serial number, job #, part #, customer name, filename..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="min-w-[180px]">
            <label className="text-xs font-medium text-gray-600 mb-1 block">Document Type</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <Button type="submit" loading={loading}>
            Search
          </Button>
        </div>
      </form>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-10"><LoadingSpinner /></div>
      ) : results.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-sm text-gray-500">
            {results.length} document{results.length !== 1 ? 's' : ''} found
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <th className="px-4 py-2">Filename</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Serial Numbers</th>
                <th className="px-3 py-2">Job</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Uploaded</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <FileIcon filename={file.filename || ''} />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{file.filename}</div>
                        <div className="text-xs text-gray-400">{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {file.document_type_display ? (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                        {file.document_type_display}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(file.serial_numbers_display || []).map((sn) => (
                        <span key={sn.id} className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                          {sn.serial_number}
                        </span>
                      ))}
                      {(file.serial_numbers_display || []).length === 0 && (
                        <span className="text-gray-300">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {file.job_number ? (
                      <button
                        onClick={() => navigate(`/jobs/${file.job}`)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        #{file.job_number}
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{file.customer_name || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="text-gray-600">{fmtDate(file.created_at)}</div>
                    {file.uploaded_by_display && (
                      <div className="text-xs text-gray-400">{file.uploaded_by_display}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleDownload(file)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : searched ? (
        <EmptyState
          title="No documents found"
          description="Try a different search term or document type filter."
        />
      ) : (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-sm">Enter a serial number, job number, or keyword to search documents.</p>
        </div>
      )}
    </div>
  );
}

function FileIcon({ filename }) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
  const isDoc = ['doc', 'docx', 'xls', 'xlsx', 'csv'].includes(ext);
  const color = isPdf ? 'text-red-500' : isImage ? 'text-green-500' : isDoc ? 'text-blue-500' : 'text-gray-400';

  return (
    <svg className={`w-4 h-4 ${color} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
